import {
    LoginRequest,
    LoginResponse,
    RefreshTokenRequest,
    RefreshTokenResponse,
    LogoutResponse,
    CurrentUserResponse,
    AuthError
} from '../types/auth';
import { BaseApiService } from './BaseApiService';
import { sharedTokenRefresh, registerTokenRefresh } from './tokenRefresh';
import { debugWarn } from '../utils/debug';

// Response wrapper for auth operations
export interface AuthApiResponse<T> {
    data: T;
    success: boolean;
}

/**
 * Signup charge / finalize answer. `requiresAction` with `clientSecret` means the bank wants
 * 3D Secure: run stripe.handleNextAction, then call signupFinalizePayment. A decline is a 400.
 */
export interface SignupChargePaymentResponse {
    success?: boolean;
    requiresAction?: boolean;
    clientSecret?: string;
    message?: string;
}

export class AuthApiService extends BaseApiService {
    constructor() {
        // Use shared token refresh function to ensure automatic refresh works everywhere
        super(sharedTokenRefresh);
    }

    /**
     * Check whether the site is currently offline to normal users (public, unauthenticated) —
     * used by the login page to show a banner before the user submits credentials.
     */
    async getSiteStatus(): Promise<{ siteOffline: boolean }> {
        const response = await this.makeRequest(this.buildUrl('/v1/auth/site-status'), { method: 'GET' });
        return await this.handleResponse<{ siteOffline: boolean }>(response);
    }

    /**
     * Authenticate user with username and password
     * @param credentials - Login credentials
     * @returns Promise containing login response with tokens
     */
    async login(credentials: LoginRequest): Promise<AuthApiResponse<LoginResponse>> {
        const response = await this.postJson('/v1/auth/login', credentials);
        const data = await this.handleResponse<LoginResponse>(response);
        return { data, success: true };
    }

    /**
     * Logout the current user
     * @returns Promise containing logout response
     */
    async logout(): Promise<AuthApiResponse<LogoutResponse>> {
        const response = await this.postJson('/v1/auth/logout', {});
        const data = await this.handleResponse<LogoutResponse>(response);
        return { data, success: true };
    }

    /**
     * Refresh access token using refresh token from HTTP-only cookies
     * The refresh token is stored in an HTTP-only cookie and sent automatically with the request
     * @param refreshToken - Not used (kept for API compatibility), refresh token comes from cookie
     * @returns Promise containing new tokens
     */
    async refreshToken(refreshToken: string): Promise<AuthApiResponse<RefreshTokenResponse>> {
        // Send empty body - refresh token is read from HTTP-only cookie by the server
        // Disable retry on 401 for refresh endpoint to avoid infinite loops
        const response = await this.postJson('/v1/auth/refresh', {}, false);
        const data = await this.handleResponse<RefreshTokenResponse>(response);
        return { data, success: true };
    }

    /**
     * Get current user information
     * @returns Promise containing current user data
     */
    async getCurrentUser(): Promise<AuthApiResponse<CurrentUserResponse>> {
        const response = await this.postJson('/v1/auth/me', {});
        const data = await this.handleResponse<CurrentUserResponse>(response);
        return { data, success: true };
    }

    /**
     * Get all teams the current user is a member of.
     * Used to populate the team switcher for Law Clerks.
     */
    async getMyTeams(): Promise<AuthApiResponse<{ teams: import('../types/auth').TeamReference[] }>> {
        const response = await this.makeRequest(this.buildUrl('/v1/auth/teams'), { method: 'GET' });
        const data = await this.handleResponse<{ teams: import('../types/auth').TeamReference[] }>(response);
        return { data, success: true };
    }

    /**
     * Switch the authenticated user's active team.
     * Re-issues JWT cookies for the selected team.
     */
    async switchTeam(teamId: string): Promise<AuthApiResponse<{ team: import('../types/auth').TeamModel }>> {
        const response = await this.postJson('/v1/auth/switch-team', { teamId });
        const data = await this.handleResponse<{ team: import('../types/auth').TeamModel }>(response);
        return { data, success: true };
    }

    /**
     * Check if user is authenticated by attempting to get current user
     * @returns Promise<boolean> - True if authenticated, false otherwise
     */
    async isAuthenticated(): Promise<boolean> {
        try {
            await this.getCurrentUser();
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Validate if user is authenticated
     * With HTTP-only cookies, we validate by making an API call
     * @returns Promise<boolean> - True if authenticated, false otherwise
     */
    async validateToken(): Promise<boolean> {
        try {
            // Try to get current user - this will use HTTP-only cookies automatically
            const response = await this.postJson('/v1/auth/me', {});

            return response.ok;
        } catch (error) {
            return false;
        }
    }

    /**
     * Note: With HTTP-only cookies, JavaScript cannot access tokens directly
     * This method is kept for compatibility but will always return null
     * @returns Object with null tokens (HTTP-only cookies are not accessible)
     */
    getTokensFromCookies(): { accessToken: string | null; refreshToken: string | null } {
        // HTTP-only cookies cannot be accessed by JavaScript
        return {
            accessToken: null,
            refreshToken: null,
        };
    }

    /**
     * Clear authentication cookies
     * Note: With HTTP-only cookies, JavaScript cannot clear them directly
     * The server should handle cookie clearing during logout
     */
    clearAuthCookies(): void {
        // HTTP-only cookies cannot be cleared by JavaScript
        // The server should handle this during logout
        debugWarn('Cannot clear HTTP-only cookies from JavaScript. Server should handle cookie clearing during logout.');
    }

    /**
     * Set authorization header for authenticated requests
     * @param token - The access token
     * @returns Headers object with Authorization header
     */
    createAuthHeaders(token: string): Record<string, string> {
        return {
            'Authorization': `Bearer ${token}`,
        };
    }

    /**
     * Note: With HTTP-only cookies, JavaScript cannot parse JWT tokens
     * These methods are kept for compatibility but will always return default values
     */
    parseTokenPayload(token: string): any | null {
        debugWarn('Cannot parse HTTP-only cookie tokens from JavaScript');
        return null;
    }

    isTokenExpired(token: string): boolean {
        debugWarn('Cannot check HTTP-only cookie token expiration from JavaScript');
        return false; // Assume not expired, let server handle validation
    }

    getTokenExpiration(token: string): number | null {
        debugWarn('Cannot get HTTP-only cookie token expiration from JavaScript');
        return null;
    }

    /**
     * Get all active users for impersonation (Root only)
     * @returns Promise containing list of active users
     */
    async getAllActiveUsers(): Promise<AuthApiResponse<{
        users: Array<{
            userId: string;
            email: string;
            firstName: string;
            lastName: string;
            fullName: string;
            teamName: string;
            teamId: string | null;
            role: string;
            businessRole: string;
            title?: string;
        }>
    }>> {
        const response = await this.postJson('/v1/auth/get-all-active-users', {});
        const data = await this.handleResponse<{
            users: Array<{
                userId: string;
                email: string;
                firstName: string;
                lastName: string;
                fullName: string;
                teamName: string;
                teamId: string | null;
                role: string;
                businessRole: string;
                title?: string;
            }>
        }>(response);
        return { data, success: true };
    }

    /**
     * Impersonate a user (Root only)
     * @param userId - The user ID to impersonate
     * @returns Promise containing impersonation token and user info
     */
    async impersonateUser(userId: string): Promise<AuthApiResponse<{
        success: boolean;
        token: string;
        refreshToken: string;
        user: {
            userId: string;
            email: string;
            firstName: string;
            lastName: string;
            fullName: string;
            role: string;
            businessRole: string;
        };
        message: string;
    }>> {
        const response = await this.postJson('/v1/auth/impersonate', { userId });
        const data = await this.handleResponse<{
            success: boolean;
            token: string;
            refreshToken: string;
            user: {
                userId: string;
                email: string;
                firstName: string;
                lastName: string;
                fullName: string;
                role: string;
                businessRole: string;
            };
            message: string;
        }>(response);
        return { data, success: true };
    }

    /**
     * Toggle subscription type in JWT token (Admin/Root only)
     * @param subscriptionType - The subscription type to set ('Pro' or 'Standard')
     * @returns Promise containing new token with updated subscription type
     */
    async toggleSubscriptionType(subscriptionType: string): Promise<AuthApiResponse<{
        success: boolean;
        token: string;
        subscriptionType: string;
        message: string;
    }>> {
        const response = await this.postJson('/v1/auth/toggle-subscription-type', { subscriptionType });
        const data = await this.handleResponse<{
            success: boolean;
            token: string;
            subscriptionType: string;
            message: string;
        }>(response);
        return { data, success: true };
    }

    /**
     * Toggle business role in JWT token (Admin/Root only)
     * @param businessRole - The business role to set ('Attorney' or 'Support Staff')
     * @returns Promise containing new token with updated business role
     */
    async toggleBusinessRole(businessRole: string): Promise<AuthApiResponse<{
        success: boolean;
        token: string;
        businessRole: string;
        message: string;
    }>> {
        const response = await this.postJson('/v1/auth/toggle-business-role', { businessRole });
        const data = await this.handleResponse<{
            success: boolean;
            token: string;
            businessRole: string;
            message: string;
        }>(response);
        return { data, success: true };
    }

    /**
     * Request a password reset email
     * @param email - The user's email address
     * @returns Promise containing success message
     */
    async forgotPassword(email: string): Promise<AuthApiResponse<{ message: string }>> {
        const response = await this.postJson('/v1/auth/forgot-password', { email });
        const data = await this.handleResponse<{ message: string }>(response);
        return { data, success: true };
    }

    /**
     * Validate a password reset token
     * @param token - The reset token to validate
     * @returns Promise containing validation result
     */
    async validateResetToken(token: string): Promise<AuthApiResponse<{ valid: boolean; message?: string }>> {
        const response = await this.postJson('/v1/auth/validate-reset-token', { token });
        const data = await this.handleResponse<{ valid: boolean; message?: string }>(response);
        return { data, success: true };
    }

    /**
     * Reset password using a reset token
     * @param token - The reset token
     * @param newPassword - The new password
     * @param confirmPassword - Password confirmation
     * @returns Promise containing success message
     */
    async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<AuthApiResponse<{ message: string }>> {
        const response = await this.postJson('/v1/auth/reset-password', {
            token,
            newPassword,
            confirmPassword
        });
        const data = await this.handleResponse<{ message: string }>(response);
        return { data, success: true };
    }

    /**
     * Validate a signup token
     * @param token - The signup token to validate
     * @returns Promise containing validation result and user info
     */
    async validateSignupToken(token: string): Promise<AuthApiResponse<{ success: boolean; userId?: string; email?: string; firstName?: string; lastName?: string; hasPasswordSet?: boolean; message?: string }>> {
        const response = await this.postJson('/v1/auth/validate-signup-token', { token });
        const data = await this.handleResponse<{ success: boolean; userId?: string; email?: string; firstName?: string; lastName?: string; hasPasswordSet?: boolean; message?: string }>(response);
        return { data, success: true };
    }

    /**
     * Complete signup by setting password and accepting terms and conditions in a single operation
     * @param token - The signup token
     * @param newPassword - The new password
     * @param confirmPassword - Password confirmation
     * @returns Promise containing success message
     */
    async completeSignup(token: string, newPassword: string, confirmPassword: string): Promise<AuthApiResponse<{ success: boolean; message?: string; userId?: string; email?: string }>> {
        const response = await this.postJson('/v1/auth/complete-signup', {
            token,
            newPassword,
            confirmPassword
        });
        const data = await this.handleResponse<{ success: boolean; message?: string; userId?: string; email?: string }>(response);
        return { data, success: true };
    }

    /**
     * Accept terms and conditions during signup (for users who already set password)
     * @param token - The signup token
     * @returns Promise containing success message
     */
    async acceptTermsAndConditions(token: string): Promise<AuthApiResponse<{ success: boolean; message?: string; userId?: string; email?: string }>> {
        const response = await this.postJson('/v1/auth/accept-terms-and-conditions', { token });
        const data = await this.handleResponse<{ success: boolean; message?: string; userId?: string; email?: string }>(response);
        return { data, success: true };
    }

    /**
     * Accept terms and conditions for an authenticated user (no token required)
     * Used when user has already logged in but hasn't accepted T&C yet
     * @returns Promise containing success message
     */
    async acceptTermsAndConditionsAuthenticated(): Promise<AuthApiResponse<{ success: boolean; message?: string; userId?: string; email?: string }>> {
        const response = await this.postJson('/v1/auth/accept-terms-and-conditions-authenticated', {});
        const data = await this.handleResponse<{ success: boolean; message?: string; userId?: string; email?: string }>(response);
        return { data, success: true };
    }

    /** Self-service signup: start (creates applicant, sends verification email). */
    async signupRegisterStart(body: {
        email: string;
        firstName: string;
        lastName: string;
        turnstileToken: string | null;
    }): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/register/start', body);
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupRegisterCheckStatus(token: string): Promise<AuthApiResponse<{ step: string }>> {
        const response = await this.postJson('/v1/signup/register/check-status', { token });
        const data = await this.handleResponse<{ step: string }>(response);
        return { data, success: true };
    }

    async signupRegisterAcceptTerms(token: string): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/register/accept-terms', { token });
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupRegisterPromoteOAuth(token: string): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/register/promote-oauth', { token });
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupRegisterPromote(token: string, password: string, turnstileToken: string | null): Promise<AuthApiResponse<{ success?: boolean; plan?: string }>> {
        const response = await this.postJson('/v1/signup/register/promote', { token, password, turnstileToken });
        const data = await this.handleResponse<{ success?: boolean; plan?: string }>(response);
        return { data, success: true };
    }

    async signupSavePlan(body: { sessionToken: string; plan: string }): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/register/save-plan', body);
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupSaveTeam(body: { sessionToken: string; teamName: string; members: Array<{ firstName: string; lastName: string; email: string; role: string; isAdminContact: boolean }>; founderRole: string; founderIsAdminContact: boolean }): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/register/save-team', body);
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupFinalizeFree(body: { sessionToken: string }): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/register/finalize-free', body);
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupCreateSetupIntent(body: { sessionToken: string }): Promise<AuthApiResponse<{ success: boolean; clientSecret: string }>> {
        const response = await this.postJson('/v1/signup/register/create-setup-intent', body);
        const data = await this.handleResponse<{ success: boolean; clientSecret: string }>(response);
        return { data, success: true };
    }

    async signupChargePayment(body: { sessionToken: string; paymentMethodId: string; paymentMethodType: string }): Promise<AuthApiResponse<SignupChargePaymentResponse>> {
        const response = await this.postJson('/v1/signup/register/charge-payment', body);
        const data = await this.handleResponse<SignupChargePaymentResponse>(response);
        return { data, success: true };
    }

    /** Completes a signup whose charge needed 3D Secure, after the browser ran the challenge. */
    async signupFinalizePayment(body: { sessionToken: string }): Promise<AuthApiResponse<SignupChargePaymentResponse>> {
        const response = await this.postJson('/v1/signup/register/finalize-payment', body);
        const data = await this.handleResponse<SignupChargePaymentResponse>(response);
        return { data, success: true };
    }

    async signupSendVerification(body: { sessionToken: string }): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/register/send-verification', body);
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupRegisterComplete(body: {
        emailVerificationToken: string;
        password: string;
        acceptTerms: boolean;
        turnstileToken: string | null;
    }): Promise<AuthApiResponse<{ userId?: string | null }>> {
        const response = await this.postJson('/v1/signup/register/complete', body);
        const data = await this.handleResponse<{ userId?: string | null }>(response);
        return { data, success: true };
    }

    async signupAcceptApplicantTeamInvite(body: {
        token: string;
        password: string;
        acceptTerms: boolean;
    }): Promise<AuthApiResponse<{ success?: boolean; plan?: string }>> {
        const response = await this.postJson('/v1/signup/invite/accept-applicant-team', body);
        const data = await this.handleResponse<{ success?: boolean; plan?: string }>(response);
        return { data, success: true };
    }

    async signupAcceptTeamSwitchInvite(token: string): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/invite/accept-team-switch', { token });
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupCheckTeamInvite(token: string): Promise<AuthApiResponse<{ status: string; email?: string }>> {
        const response = await this.postJson('/v1/signup/invite/check-team', { token });
        const data = await this.handleResponse<{ status: string; email?: string }>(response);
        return { data, success: true };
    }

    async signupNotifyExpiredInvite(token: string): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/invite/notify-expired', { token });
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    async signupResendTeamInvite(token: string): Promise<AuthApiResponse<{ success?: boolean }>> {
        const response = await this.postJson('/v1/signup/invite/resend', { token });
        const data = await this.handleResponse<{ success?: boolean }>(response);
        return { data, success: true };
    }

    /** Returns Google authorization URL; browser should navigate to it. */
    async signupOAuthGoogleStart(returnPath?: string): Promise<AuthApiResponse<{ authorizationUrl?: string; configured?: boolean }>> {
        const response = await this.postJson('/v1/signup/oauth/google/start', { returnPath: returnPath ?? '/auth/register/complete' });
        const data = await this.handleResponse<{ authorizationUrl?: string; configured?: boolean }>(response);
        return { data, success: true };
    }

    async signupOAuthAppleStart(returnPath?: string): Promise<AuthApiResponse<{ authorizationUrl?: string; configured?: boolean; message?: string }>> {
        const response = await this.postJson('/v1/signup/oauth/apple/start', { returnPath: returnPath ?? '/auth/register/verify' });
        const data = await this.handleResponse<{ authorizationUrl?: string; configured?: boolean; message?: string }>(response);
        return { data, success: true };
    }

    async signupOAuthMicrosoftStart(returnPath?: string): Promise<AuthApiResponse<{ authorizationUrl?: string; configured?: boolean }>> {
        const response = await this.postJson('/v1/signup/oauth/microsoft/start', { returnPath: returnPath ?? '/auth/register/complete' });
        const data = await this.handleResponse<{ authorizationUrl?: string; configured?: boolean }>(response);
        return { data, success: true };
    }

    /** Returns Google authorization URL for login; browser should navigate to it. */
    async authOAuthGoogleLoginStart(returnPath?: string): Promise<AuthApiResponse<{ authorizationUrl?: string; configured?: boolean }>> {
        const response = await this.postJson('/v1/auth/oauth/google/start', { returnPath: returnPath ?? '/cases/list' });
        const data = await this.handleResponse<{ authorizationUrl?: string; configured?: boolean }>(response);
        return { data, success: true };
    }

    async authOAuthAppleLoginStart(returnPath?: string): Promise<AuthApiResponse<{ authorizationUrl?: string; configured?: boolean; message?: string }>> {
        const response = await this.postJson('/v1/auth/oauth/apple/start', { returnPath: returnPath ?? '/cases/list' });
        const data = await this.handleResponse<{ authorizationUrl?: string; configured?: boolean; message?: string }>(response);
        return { data, success: true };
    }

    async authOAuthMicrosoftLoginStart(returnPath?: string): Promise<AuthApiResponse<{ authorizationUrl?: string; configured?: boolean }>> {
        const response = await this.postJson('/v1/auth/oauth/microsoft/start', { returnPath: returnPath ?? '/cases/list' });
        const data = await this.handleResponse<{ authorizationUrl?: string; configured?: boolean }>(response);
        return { data, success: true };
    }
}

// Singleton instance
export const authApiService = new AuthApiService();

// Register refresh implementation so tokenRefresh does not need to import this module
registerTokenRefresh(() =>
    authApiService.refreshToken('http-only-cookie').then(() => true).catch(() => false)
);

// Register 403 handler so BaseApiService does not need to import this module
BaseApiService.setOn403Handler(() => authApiService.getCurrentUser().then(() => {}));
